import { uuid } from '../constants/constants';
import { columnGapPx, pageHeight, pageWidth } from '../constants/gridConfig';
import { getColumnById, getContainerByColumnId } from '../store/lookups';
import { mutateAddBoxes, mutateRemoveBox } from './boxes';

export const newColumn = ({
  size = 1,
  drawMode = false,
  isOldPage = false,
  backgroundColor = 'transparent',
  maskId,
}) => {
  const id = uuid();
  return {
    id,
    size,
    drawMode,
    isOldPage,
    backgroundColor,
    maskId,
  };
};

export const mutateAddColumns = (draft, newColumns = [], boxes = []) => {
  draft.columns = draft.columns.concat(newColumns);
  newColumns.forEach((column, i) => {
    // Les appelants passent parfois un trou (colonne sans box à poser, cf. le
    // collage d'un container qui a moins de box que de colonnes) : sans ce
    // filtre, `{ ...undefined, columnId }` créait une box fantôme, sans type.
    const newBoxes = boxes
      .filter(Boolean)
      .map((box) => ({ ...box, columnId: column.id }));
    mutateAddBoxes({ draft, newBoxes, drawMode: column.drawMode });
  });
};

export const mutateDeleteColumns = (draft, columnsToDeleteIds = []) => {
  // Lignes imbriquées dans les colonnes supprimées : elles n'existent que par
  // leur colonne porteuse, donc elles partent avec (et leurs propres colonnes,
  // via la récursion mutuelle avec mutateRemoveRowContainers).
  const nestedRowIds = [];
  draft.columns.forEach((c) => {
    if (columnsToDeleteIds.includes(c.id) && c.rowContainerIds?.length) {
      nestedRowIds.push(...c.rowContainerIds);
    }
  });
  draft.boxes
    .filter((c) => columnsToDeleteIds.includes(c.columnId))
    .forEach((box) => mutateRemoveBox(draft, box.id));
  draft.columns = draft.columns.filter(
    (c) => !columnsToDeleteIds.includes(c.id)
  );
  if (nestedRowIds.length) mutateRemoveRowContainers(draft, nestedRowIds);
};

export const mutateRemoveRowContainers = (draft, rowContainerIds = []) => {
  if (!draft.rowContainers?.length || !rowContainerIds.length) return;
  const idsToRemove = new Set(rowContainerIds);
  const columnsToDeleteIds = [];
  draft.rowContainers = draft.rowContainers.filter((row) => {
    if (!idsToRemove.has(row.id)) return true;
    columnsToDeleteIds.push(...row.columnsIds);
    return false;
  });
  if (columnsToDeleteIds.length) mutateDeleteColumns(draft, columnsToDeleteIds);
};

// Répartition verticale des box d'une cellule, hors mode dessin (cf.
// setColumnVerticalAlign). `center` = valeur historique, donc défaut implicite.
export const COLUMN_VERTICAL_ALIGNS = {
  TOP: 'top',
  CENTER: 'center',
  BOTTOM: 'bottom',
  BETWEEN: 'between',
};

/**
 * Place la pile de box dans la hauteur de la cellule (axe transversal du flex
 * de la cellule). `between` s'étire sur toute la hauteur : c'est alors la pile
 * elle-même qui répartit ses box, cf. columnStackStyle.
 */
export const columnStackAlignItems = (verticalAlign) => {
  switch (verticalAlign) {
    case COLUMN_VERTICAL_ALIGNS.TOP:
      return 'flex-start';
    case COLUMN_VERTICAL_ALIGNS.BOTTOM:
      return 'flex-end';
    case COLUMN_VERTICAL_ALIGNS.BETWEEN:
      return 'stretch';
    default:
      return 'center';
  }
};

/**
 * Style de la pile de box d'une cellule hors mode dessin. Seul `between` change
 * quelque chose : la pile devient une colonne flex pleine hauteur, première box
 * en haut, dernière en bas, espaces égaux entre les autres. Les autres réglages
 * se jouent sur la cellule (columnStackAlignItems), la pile gardant sa hauteur
 * naturelle.
 */
export const columnStackStyle = ({ verticalAlign, boxesCount = 0 } = {}) => {
  if (verticalAlign !== COLUMN_VERTICAL_ALIGNS.BETWEEN || boxesCount < 2)
    return {};
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  };
};

/**
 * Cellule dont la répartition verticale s'applique à une box donnée, hors mode
 * dessin. Deux cas seulement produisent quelque chose à répartir :
 * - la cellule de la box porte plusieurs box → on répartit ces box ;
 * - la box est seule dans la cellule d'une ligne imbriquée, et la colonne
 *   porteuse compte plusieurs lignes → on répartit ces lignes (c'est la
 *   colonne parente qui porte le réglage, pas la cellule de la box).
 * Sinon undefined : une box seule dans une colonne simple n'a rien à répartir,
 * son propre alignement (box.positions) fait le travail.
 */
export const resolveVerticalAlignTarget = ({
  box,
  columns = [],
  boxes = [],
  rowContainers = [],
}) => {
  if (!box?.columnId) return undefined;
  const column = columns.find((c) => c.id === box.columnId);
  if (!column || column.drawMode) return undefined;

  const boxesInColumn = boxes.filter((b) => b.columnId === column.id).length;
  if (boxesInColumn > 1) return { column, kind: 'boxes' };

  const row = rowContainers.find((r) => r.columnsIds?.includes(column.id));
  const parentColumn = row
    ? columns.find((c) => c.id === row.parentColumnId)
    : undefined;
  if ((parentColumn?.rowContainerIds?.length ?? 0) > 1)
    return { column: parentColumn, kind: 'rows' };

  return undefined;
};

/**
 * Écart horizontal entre les colonnes d'un container, en px. Réglable par
 * container depuis le panneau de disposition ; absent, c'est la constante
 * historique. Les lignes imbriquées suivent leur container top-level.
 */
export const containerColumnGap = (container) =>
  container?.columnGap ?? columnGapPx;

export const computeColumnWidth = ({
  drawMode = false,
  marginLeft,
  marginRight,
  containerColumnsLength = 1,
  size = 1,
  landscape = false,
  columnGap = columnGapPx,
  // Colonne imbriquée (sous-colonne d'une ligne) : la largeur de référence est
  // celle de la colonne parente, pas celle de la page.
  parentWidthPx,
}) =>
  // Comme le rendu flex : les (n - 1) écarts sortent de la largeur avant
  // partage, chaque colonne en cède sa part. La marge de sécurité finale reste
  // la constante historique, quel que soit l'écart : la retirer à l'écart
  // réglé rognait chaque colonne d'un écart entier, une fois de plus à chaque
  // niveau de ligne imbriquée. À l'écart par défaut, le calcul est inchangé.
  ((parentWidthPx ?? (landscape ? pageHeight : pageWidth)) -
    (drawMode ? 0 : marginLeft + marginRight) -
    (containerColumnsLength - 1) * columnGap) *
    size -
  columnGapPx;

/**
 * Container top-level hébergeant une colonne, en remontant la chaîne des
 * lignes imbriquées le cas échéant. Undefined si la colonne appartient à un
 * mask ou n'existe plus.
 */
export const getTopLevelContainer = ({
  columnId,
  containers,
  rowContainers = [],
}) => {
  let currentColumnId = columnId;
  while (currentColumnId) {
    const container = getContainerByColumnId(containers, currentColumnId);
    if (container) return container;
    const row = getContainerByColumnId(rowContainers, currentColumnId);
    if (!row) return undefined;
    currentColumnId = row.parentColumnId;
  }
  return undefined;
};

/**
 * Ids de TOUTES les colonnes d'un container : les siennes et celles des lignes
 * imbriquées, récursivement. Compter le contenu d'un container ou le vider
 * demande cette descente — ses `columnsIds` ne portent que le premier niveau.
 */
export const getContainerColumnIds = ({
  container,
  columns = [],
  rowContainers = [],
}) => {
  const columnIds = [];
  const walk = (ids = []) => {
    ids.forEach((columnId) => {
      columnIds.push(columnId);
      const column = columns.find((c) => c.id === columnId);
      (column?.rowContainerIds ?? []).forEach((rowId) => {
        const row = rowContainers.find((r) => r.id === rowId);
        walk(row?.columnsIds ?? []);
      });
    });
  };
  walk(container?.columnsIds ?? []);
  return columnIds;
};

/**
 * Largeur disponible (px) pour le contenu d'une colonne, qu'elle soit portée
 * par un container top-level ou par une ligne imbriquée (remonte alors la
 * chaîne des parents). Les marges de page ne s'appliquent qu'au niveau
 * top-level : une sous-colonne se partage la largeur déjà démarginée de sa
 * colonne parente.
 */
export const getColumnAvailableWidth = ({
  columnId,
  columns,
  containers,
  rowContainers = [],
  marginLeft,
  marginRight,
  landscape = false,
  // Absent : l'écart du container top-level qui porte la colonne.
  columnGap,
}) => {
  const column = getColumnById(columns, columnId);
  if (!column) return 0;
  const gap =
    columnGap ??
    containerColumnGap(
      getTopLevelContainer({ columnId, containers, rowContainers })
    );

  const container = getContainerByColumnId(containers, columnId);
  if (container) {
    return computeColumnWidth({
      landscape,
      drawMode: column.drawMode,
      marginLeft,
      marginRight,
      containerColumnsLength: container.columnsIds.length,
      size: column.size,
      columnGap: gap,
    });
  }

  const row = getContainerByColumnId(rowContainers, columnId);
  if (!row) return 0;
  return computeColumnWidth({
    parentWidthPx: getColumnAvailableWidth({
      columnId: row.parentColumnId,
      columns,
      containers,
      rowContainers,
      marginLeft,
      marginRight,
      landscape,
      columnGap: gap,
    }),
    drawMode: column.drawMode,
    marginLeft: 0,
    marginRight: 0,
    containerColumnsLength: row.columnsIds.length,
    size: column.size,
    columnGap: gap,
  });
};

export const mutateUpdateColumn = (draft, updatedColumn) => {
  draft.columns = draft.columns.map((c) => {
    if (c.id === updatedColumn.id) {
      return updatedColumn;
    }
    return c;
  });
};
